import React, { useState, useEffect } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { formatDateForDisplay } from '../../utils/dateUtils';
import { FormCard, FormGrid, Field, TextInput } from '../../components/ui/form';
import { IconPlus, IconWallet } from '../../components/ui/icons';
import { tokens } from '../../theme';

const createEmptyCheck = () => ({
    id: Date.now(),
    number: '',
    bank: '',
    branch: '',
    issueDate: undefined,
    paymentDate: undefined,
    signer: '',
    cuit: '',
    notes: '',
    amount: '',
});

/**
 * Carga de cheques dentro de una forma de pago.
 *
 * Mismo componente que en Registrar pago; acá además puede arrancar con un
 * cheque ya abierto (`autoAddFirstCheck`) cuando se entra eligiendo esa forma
 * de pago.
 */
export default function CheckManager({ setPaymentMethods, handleInputChange, setChecks, autoAddFirstCheck = false }) {
    const [checks, setChecksLocal] = useState([]);

    useEffect(() => {
        if (autoAddFirstCheck && checks.length === 0) {
            const newCheck = createEmptyCheck();
            setChecksLocal([newCheck]);
            setChecks([newCheck]);
        }
    }, [autoAddFirstCheck]);

    useEffect(() => {
        const totalAmount = checks.reduce((acc, check) => acc + parseFloat(check.amount || 0), 0);
        setPaymentMethods(prev => ({ ...prev, cheques: totalAmount }));
    }, [checks]);

    const addCheck = () => {
        const newCheck = createEmptyCheck();
        const updatedChecks = [...checks, newCheck];
        setChecksLocal(updatedChecks);
        setChecks(updatedChecks);
    };

    const removeCheck = (id) => {
        const updatedChecks = checks.filter((check) => check.id !== id);
        setChecksLocal(updatedChecks);
        setChecks(updatedChecks);
        const totalAmount = updatedChecks.reduce((acc, check) => acc + parseFloat(check.amount || 0), 0);
        setPaymentMethods(prev => ({ ...prev, cheques: totalAmount }));
        handleInputChange({ target: { id: 'cheques', value: totalAmount.toString() } });
    };

    const updateCheck = (id, field, value) => {
        if (field === 'amount') {
            // Validar que solo se permitan números y un único punto decimal
            if (!/^(\d*\.?\d*)?$/.test(value)) {
                return; // No actualizar si contiene caracteres no permitidos (como comas)
            }

            const updatedChecks = checks.map((check) =>
                check.id === id ? { ...check, [field]: value } : check
            );
            const totalAmount = updatedChecks.reduce((acc, check) => acc + parseFloat(check.amount || 0), 0);
            setChecksLocal(updatedChecks);
            setChecks(updatedChecks);
            setPaymentMethods(prev => ({ ...prev, cheques: totalAmount }));
            handleInputChange({ target: { id: 'cheques', value: totalAmount.toString() } });
        } else {
            const updatedChecks = checks.map((check) =>
                check.id === id ? { ...check, [field]: value } : check
            );
            setChecksLocal(updatedChecks);
            setChecks(updatedChecks);
        }
    };

    // Manejador específico para el campo de monto que previene la entrada de comas
    const handleAmountChange = (e, id) => {
        const value = e.target.value;

        // Reemplazar cualquier coma por punto
        const sanitizedValue = value.replace(/,/g, '.');

        // Asegurar que solo haya un punto decimal
        const parts = sanitizedValue.split('.');
        const finalValue = parts.length > 2
            ? parts[0] + '.' + parts.slice(1).join('')
            : sanitizedValue;

        // Validar que sea un número válido con punto decimal
        if (!/^(\d*\.?\d*)?$/.test(finalValue)) {
            return; // No actualizar si no es un formato válido
        }

        updateCheck(id, 'amount', finalValue);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%', minWidth: 0 }}>
            {checks.length === 0 ? (
                <Box
                    sx={{
                        border: `1px dashed ${tokens.borderStrong}`,
                        borderRadius: `${tokens.radiusCard}px`,
                        padding: '20px 18px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '10px',
                        textAlign: 'center',
                    }}
                >
                    <Box sx={{ color: tokens.ink4, display: 'flex' }}>
                        <IconWallet size={22} />
                    </Box>
                    <Typography sx={{ fontSize: '12.5px', color: tokens.ink3, maxWidth: 320 }}>
                        Si parte del pago entra en cheques, cargalos acá. El total se suma solo a
                        las formas de pago.
                    </Typography>
                    <Button variant="outlined" startIcon={<IconPlus size={15} />} onClick={addCheck}>
                        Agregar cheque
                    </Button>
                </Box>
            ) : (
                <>
                    {checks.map((check, index) => (
                        <FormCard
                            key={check.id}
                            title={`Cheque ${index + 1}`}
                            action={
                                <Button
                                    variant="text"
                                    onClick={() => removeCheck(check.id)}
                                    sx={{ color: tokens.brandDark, minWidth: 'auto' }}
                                >
                                    Eliminar
                                </Button>
                            }
                        >
                            <FormGrid columns={2}>
                                <Field label="Número">
                                    <TextInput
                                        id={`number-${check.id}`}
                                        numeric
                                        value={check.number}
                                        onChange={(e) => updateCheck(check.id, 'number', e.target.value)}
                                    />
                                </Field>
                                <Field label="Banco">
                                    <TextInput
                                        id={`bank-${check.id}`}
                                        value={check.bank}
                                        onChange={(e) => updateCheck(check.id, 'bank', e.target.value)}
                                    />
                                </Field>
                                <Field label="Sucursal">
                                    <TextInput
                                        id={`branch-${check.id}`}
                                        value={check.branch}
                                        onChange={(e) => updateCheck(check.id, 'branch', e.target.value)}
                                    />
                                </Field>
                                <Field label="Firmante">
                                    <TextInput
                                        id={`signer-${check.id}`}
                                        value={check.signer}
                                        onChange={(e) => updateCheck(check.id, 'signer', e.target.value)}
                                    />
                                </Field>
                                <Field label="Fecha de emisión">
                                    <TextInput
                                        id={`issue-${check.id}`}
                                        type="date"
                                        value={formatDateForDisplay(check.issueDate)}
                                        onChange={(e) => updateCheck(check.id, 'issueDate', e.target.value)}
                                    />
                                </Field>
                                <Field label="Fecha de pago">
                                    <TextInput
                                        id={`payment-${check.id}`}
                                        type="date"
                                        value={formatDateForDisplay(check.paymentDate)}
                                        onChange={(e) => updateCheck(check.id, 'paymentDate', e.target.value)}
                                    />
                                </Field>
                                <Field label="CUIT del librador">
                                    <TextInput
                                        id={`cuit-${check.id}`}
                                        numeric
                                        value={check.cuit}
                                        onChange={(e) => updateCheck(check.id, 'cuit', e.target.value)}
                                    />
                                </Field>
                                <Field label="Monto">
                                    <TextInput
                                        id={`amount-${check.id}`}
                                        numeric
                                        inputMode="decimal"
                                        placeholder="0.00"
                                        value={check.amount}
                                        onChange={(e) => handleAmountChange(e, check.id)}
                                    />
                                </Field>
                                <Field label="Notas" span={2}>
                                    <TextInput
                                        id={`notes-${check.id}`}
                                        value={check.notes}
                                        onChange={(e) => updateCheck(check.id, 'notes', e.target.value)}
                                    />
                                </Field>
                            </FormGrid>
                        </FormCard>
                    ))}

                    <Box>
                        <Button variant="outlined" startIcon={<IconPlus size={15} />} onClick={addCheck}>
                            Agregar otro cheque
                        </Button>
                    </Box>
                </>
            )}
        </Box>
    );
}
